/**
 * Host-side resolution: predicates, collections, data paths and values.
 *
 * Everything a template can read is computed here, once. A template never
 * re-derives a rule — which is what stops the RSVP cutoff from meaning two
 * different things on two different designs (`index.tsx:75` compares dates at
 * midnight, `golden-serenity/utils.ts:23` compares timestamps; this module is
 * the single answer).
 */

import type { EventCeremony } from "@/services/api/eventSettings";
import type { PublicEvent, PublicGiftItem } from "@/services/api/publicEvents";
import { countdownParts } from "@/lib/countdown";
import { applyFormatter, NAME_PART_SEPARATOR } from "./formatters";
import type {
  CeremonyEntry,
  CountdownPart,
  EngineContext,
  GiftCategoryEntry,
  RepeatScope,
  TemplateValue,
} from "./types";

const CEREMONY_ORDER: Array<CeremonyEntry["key"]> = ["civil", "religious", "baptism", "party"]; // JUB-154: separate baptism slot

/** Empty per MASTER_TEMPLATE §6.2: null, undefined, an empty array or a blank string. */
const isEmptyValue = (value: unknown): boolean =>
  value === null
  || value === undefined
  || (Array.isArray(value) && value.length === 0)
  || String(value).trim() === "";

const isFilled = (value: unknown): boolean => !isEmptyValue(value);

const startOfDay = (value: Date): number => {
  const copy = new Date(value.getTime());
  copy.setHours(0, 0, 0, 0);
  return copy.getTime();
};

/* --------------------------------------------------------------- derived */

export function resolveTagline(event: PublicEvent, locale: string): string {
  const code = (locale || "ro") as "ro" | "en";
  return event.heroText?.[code] || event.heroText?.ro || event.heroText?.en || "";
}

export function buildCeremonies(event: PublicEvent): CeremonyEntry[] {
  return CEREMONY_ORDER.flatMap((key) => {
    const ceremony = event.ceremonies?.[key] as EventCeremony | undefined;
    if (!ceremony?.enabled) return [];

    const addressParts = [ceremony.address, ceremony.venue, ceremony.city].filter(Boolean);
    const datetime = ceremony.datetime || null;

    return [{
      key,
      name: ceremony.name ?? "",
      datetime,
      venue: ceremony.venue ?? null,
      address: ceremony.address ?? null,
      city: ceremony.city ?? null,
      notes: ceremony.notes ?? null,
      mapsUrl: addressParts.length
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressParts.join(", "))}`
        : null,
      hasTime: Boolean(datetime && datetime.includes("T") && datetime.includes(":")),
    }];
  });
}

/** City of the first enabled ceremony, then the localized country name. */
export function resolveLocation(event: PublicEvent, locale: string): string {
  const firstCity = buildCeremonies(event).find((entry) => isFilled(entry.city))?.city ?? null;
  const country = applyFormatter("country.display", event.country, { locale });
  return [firstCity, country].filter(isFilled).join(", ");
}

/** Name parts for the monogram, separated so `text.initials` can split them. */
export function resolveInitialsSource(event: PublicEvent): string {
  if (event.starBride && event.starGroom) {
    return [event.starBride, event.starGroom].join(NAME_PART_SEPARATOR);
  }
  return (event.name ?? "").split("&").map((part) => part.trim()).filter(Boolean).join(NAME_PART_SEPARATOR);
}

export function buildStoryParagraphs(event: PublicEvent): Array<{ text: string; index: number }> {
  const source = event.story || event.description || "";
  return source
    .split("\n\n")
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((text, index) => ({ text, index: index + 1 }));
}

// JUB-176: the countdown rule lives in `src/lib/countdown.ts`, shared with the
// TSX templates, the dashboard card and the onboarding wizard.
export { computeCountdown } from "@/lib/countdown";

/* ------------------------------------------------------------ predicates */

export function buildPredicates(ctx: EngineContext): Record<string, boolean> {
  const { event, locale, now } = ctx;

  const storyPresent = isFilled(event.story) || isFilled(event.description);
  const galleryPresent = (event.gallery?.length ?? 0) > 0;
  const ceremonies = buildCeremonies(event);

  const rsvpEnabled = event.rsvpSettings?.rsvpEnabled === true;
  // Normative definition. Date-only comparison, so an event stays open for the
  // whole of its cutoff day on every template.
  const rsvpClosed = Boolean(event.rsvpCutoffDate) && startOfDay(now) > startOfDay(new Date(event.rsvpCutoffDate as string));

  const registryAllowed = Boolean(event.registryPublic) && Boolean(event.giftsActive);
  const registryVisible = registryAllowed && Boolean(event.hasEnabledGifts);

  const includesWedding = event.includesWedding ?? true;
  const includesBaptism = event.includesBaptism ?? false;

  return {
    "cover.present": isFilled(event.cover),
    "date.present": Boolean(event.date) && !Number.isNaN(new Date(event.date ?? "").getTime()),
    "hashtag.present": isFilled(event.hashtag),
    "tagline.present": isFilled(resolveTagline(event, locale)),
    "location.present": isFilled(resolveLocation(event, locale)),

    "story.present": storyPresent,
    "story.visible": storyPresent && (event.showStory ?? true),
    "gallery.present": galleryPresent,
    "gallery.visible": galleryPresent && (event.showGallery ?? true),
    "timeline.present": ceremonies.length > 0,
    "timeline.visible": ceremonies.length > 0 && (event.showTimeline ?? true),

    "family.parents.present": isFilled(event.family?.parents),
    "family.godparents.present": isFilled(event.family?.godparents),

    "rsvp.enabled": rsvpEnabled,
    "rsvp.closed": rsvpClosed,
    "rsvp.open": rsvpEnabled && !rsvpClosed,
    "rsvp.dueDate.present": isFilled(event.rsvpSettings?.dueDate || event.rsvpCutoffDate),

    "registry.allowed": registryAllowed,
    "registry.visible": registryVisible,
    "registry.loading": ctx.gifts.loading,
    "registry.hasItems": filterGifts(ctx).length > 0,
    "registry.categories.multiple": buildGiftCategories(ctx).length > 1,
    "payments.card": ctx.gifts.cardPaymentsEnabled === true,

    "mode.wedding": includesWedding && !includesBaptism,
    "mode.baptismOnly": includesBaptism && !includesWedding,
    "mode.weddingAndBaptism": includesBaptism && includesWedding,

    "names.couple": isFilled(event.starBride) && isFilled(event.starGroom),
    "names.baptism": isFilled(event.starBaptism),

    "motion.reduced": ctx.prefersReducedMotion,
    "locale.ro": locale === "ro",
    "locale.en": locale === "en",
    "preview.draft": false,
  };
}

/* ----------------------------------------------------------- collections */

export function filterGifts(ctx: EngineContext): PublicGiftItem[] {
  const { items, activeCategory } = ctx.gifts;
  if (activeCategory === "all") return items;
  return items.filter(
    (gift) => gift.category?.id === activeCategory || gift.category?.name === activeCategory,
  );
}

export function buildGiftCategories(ctx: EngineContext): GiftCategoryEntry[] {
  return [
    { id: "all", name: ctx.t("events.form.template.allItems"), active: ctx.gifts.activeCategory === "all" },
    ...ctx.gifts.categories.map((category) => ({
      id: category.id ?? category.name,
      name: category.name,
      active: ctx.gifts.activeCategory === (category.id ?? category.name),
    })),
  ];
}

export function buildCountdownParts(ctx: EngineContext): CountdownPart[] {
  const parts = countdownParts(ctx.event.date, ctx.now);
  return parts.map((part, index) => ({
    unit: part.unit,
    value: part.value,
    // JUB-94: singular/plural per unit, kept in parity with the golden_serenity
    // TSX template's own countdown (golden-serenity/Countdown.tsx) via the
    // same `src/lib/countdown.ts` key resolution.
    label: ctx.t(part.labelKey),
    last: index === parts.length - 1,
  }));
}

/** Resolve a collection name to its items plus the loop scope each item exposes. */
export function resolveCollection(name: string, ctx: EngineContext): RepeatScope[] {
  switch (name) {
    case "story.paragraphs":
      return buildStoryParagraphs(ctx.event).map((paragraph) => ({
        "paragraph.text": paragraph.text,
        "paragraph.index": paragraph.index,
      }));

    case "ceremonies":
      return buildCeremonies(ctx.event).map((ceremony) => ({
        "ceremony.name": ceremony.name,
        "ceremony.datetime": ceremony.datetime,
        "ceremony.venue": ceremony.venue,
        "ceremony.address": ceremony.address,
        "ceremony.city": ceremony.city,
        "ceremony.notes": ceremony.notes,
        "ceremony.mapsUrl": ceremony.mapsUrl,
        "ceremony.hasTime": ceremony.hasTime,
      }));

    case "gallery":
      return (ctx.event.gallery ?? []).map((src, index) => ({
        "photo.src": src,
        "photo.index": index + 1,
        $photoIndex: index,
      }));

    case "gifts":
      return filterGifts(ctx).map((gift) => ({
        "gift.name": gift.name,
        "gift.description": gift.description ?? null,
        "gift.cover": gift.cover ?? null,
        "gift.hasCover": Boolean(gift.cover),
        "gift.category.name": gift.category?.name ?? null,
        $gift: gift,
      }));

    case "giftCategories":
      return buildGiftCategories(ctx).map((category) => ({
        "category.id": category.id,
        "category.name": category.name,
        "category.active": category.active,
      }));

    case "countdown.parts":
      return buildCountdownParts(ctx).map((part) => ({
        "part.value": part.value,
        "part.label": part.label,
        "part.unit": part.unit,
        "part.last": part.last,
      }));

    default:
      throw new Error(`Unknown collection: ${name}`);
  }
}

/* ------------------------------------------------------------ data paths */

export function resolvePath(path: string, ctx: EngineContext, scope: RepeatScope): unknown {
  if (path in scope) return scope[path];

  const { event, locale } = ctx;
  switch (path) {
    case "event.name": return event.name;
    case "event.date": return event.date;
    case "event.city": return event.city;
    case "event.country": return event.country;
    case "event.venue": return event.venue;
    case "event.cover": return event.cover;
    case "event.story": return event.story || event.description;
    case "event.description": return event.description;
    case "event.hashtag": return event.hashtag;
    case "event.starBride": return event.starBride;
    case "event.starGroom": return event.starGroom;
    case "event.starBaptism": return event.starBaptism;
    case "event.starBaptism2": return event.starBaptism2;
    case "event.tagline": return resolveTagline(event, locale);
    case "event.location": return resolveLocation(event, locale);
    case "event.initials": return resolveInitialsSource(event);
    case "event.family.parents": return event.family?.parents;
    case "event.family.godparents": return event.family?.godparents;
    case "event.rsvp.dueDate": return event.rsvpSettings?.dueDate || event.rsvpCutoffDate;
    default:
      throw new Error(`Unknown data path: ${path}`);
  }
}

/* ---------------------------------------------------------------- values */

function interpolate(template: string, params: Record<string, string>): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key: string) => params[key] ?? "");
}

/** Resolve a template value to a display string. Empty means "render nothing". */
export function resolveValue(value: TemplateValue, ctx: EngineContext, scope: RepeatScope = {}): string {
  if ("bind" in value) {
    // Bound data is empty when blank, not only when absent: an owner who typed a
    // space into a venue field must not get an empty line. A formatter that
    // produces nothing (an unknown country) is empty too.
    const raw = resolvePath(value.bind, ctx, scope);
    if (isEmptyValue(raw)) return "";
    const text = value.fmt
      ? applyFormatter(value.fmt, raw, { locale: ctx.locale, sep: value.sep })
      : String(raw);
    return isEmptyValue(text) ? "" : text;
  }

  if ("i18n" in value) {
    const key = value.i18n.startsWith("tpl.") ? value.i18n.slice(4) : value.i18n;
    const template = ctx.strings[key];
    if (template === undefined) return value.i18n; // surfaces a missing key instead of hiding it
    if (!value.params) return template;
    const params: Record<string, string> = {};
    for (const [name, param] of Object.entries(value.params)) {
      params[name] = resolveValue(param, ctx, scope);
    }
    return interpolate(template, params);
  }

  if ("join" in value) {
    const separator = value.sep ?? " ";
    return value.join
      .map((part) => resolveValue(part, ctx, scope))
      .filter((part) => part !== "")
      .join(separator);
  }

  // first
  for (const candidate of value.first) {
    const resolved = resolveValue(candidate, ctx, scope);
    if (resolved !== "") return resolved;
  }
  return "";
}
